import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { Stack, type Theme } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import {
  type CancellationProtectionPayoutSourceEvent,
  type ResolvedCancellationProtectionPayout,
} from "@src/appV2/redesign/CancellationProtectionPayout/api/types";
import { getTotalPayoutAmountInMinorUnits } from "@src/appV2/redesign/CancellationProtectionPayout/api/utils";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";
import { differenceInHours } from "date-fns";
import pluralize from "pluralize";

interface OthersPayoutCardProps {
  payout: ResolvedCancellationProtectionPayout;
}

export function OthersPayoutCard(props: OthersPayoutCardProps) {
  const { payout } = props;

  const originalPayoutAmount = payout.originalPayRate.amountInMinorUnits * payout.originalPaidHours;
  const totalPayoutAmount = getTotalPayoutAmountInMinorUnits(payout);

  return (
    <Stack
      aria-label="Others payout card"
      sx={(theme: Theme) => ({
        borderRadius: theme.borderRadius?.medium,
        border: `${theme.borderWidth?.regular} solid ${theme.palette.background.secondary}`,
        overflow: "hidden",
        width: "100%",
      })}
    >
      <Stack
        spacing={5}
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.background.tertiary,
          paddingY: 5,
          paddingX: 6,
          borderTopLeftRadius: theme.borderRadius?.medium,
          borderTopRightRadius: theme.borderRadius?.medium,
        })}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={2}>
            {payout.shift?.start && (
              <Text
                semibold
                variant="body2"
                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
              >
                {formatShortDateWithWeekday(payout.shift.start, {
                  timeZone: payout.workplace?.tmz,
                })}
              </Text>
            )}
            {payout.shift?.qualification && (
              <Text
                semibold
                variant="body2"
                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
              >
                {payout.shift.qualification}
              </Text>
            )}
          </Stack>
          <Text variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}>
            {getSourceEventDescription(payout.sourceEvent, payout.shift)}
          </Text>
        </Stack>

        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>
              {getPayoutLabel(payout.sourceEvent, payout.originalPaidHours)}
            </Text>
            <Text
              semibold
              variant="body2"
              sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
            >
              {formatCentsAsUsd(originalPayoutAmount)}
            </Text>
          </Stack>

          {payout.bonusAmount.amountInMinorUnits > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Text
                variant="body2"
                sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
              >
                With Cancellation Protection
              </Text>
              <Text
                semibold
                variant="body2"
                sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
              >
                + {formatCentsAsUsd(payout.bonusAmount.amountInMinorUnits)}
              </Text>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.background.tertiary,
          borderTop: `${theme.borderWidth?.regular} solid ${theme.palette.background.secondary}`,
          paddingX: 6,
          paddingY: 5,
          borderBottomLeftRadius: theme.borderRadius?.medium,
          borderBottomRightRadius: theme.borderRadius?.medium,
        })}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CbhIcon type="parachute" size="small" />
          <Text
            semibold
            variant="body1"
            sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
          >
            Earnings protected
          </Text>
        </Stack>
        <Text semibold variant="h6" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
          {formatCentsAsUsd(totalPayoutAmount)}
        </Text>
      </Stack>
    </Stack>
  );
}

function getSourceEventDescription(
  sourceEvent: CancellationProtectionPayoutSourceEvent,
  shift?: { cancelledAt?: Date; start?: Date }
): string {
  switch (sourceEvent) {
    case "SHIFT_SENT_HOME": {
      return "Sent home by workplace";
    }
    default: {
      if (shift?.start && shift?.cancelledAt) {
        const hours = differenceInHours(shift.start, shift.cancelledAt);
        if (hours > 0) {
          return `Shift cancelled ${hours} ${pluralize("hour", hours)} before start`;
        }

        return "Shift cancelled just before start";
      }

      return "Shift cancelled";
    }
  }
}

function getPayoutLabel(
  sourceEvent: CancellationProtectionPayoutSourceEvent,
  originalPaidHours: number
): string {
  switch (sourceEvent) {
    case "SHIFT_SENT_HOME": {
      return "Sent home payout";
    }
    default: {
      return `${originalPaidHours}hr cancellation payout`;
    }
  }
}
